import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Alert, Card, Row, Col, Spinner, Image } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useAdminMunicipalitySetting,
  useUpdateAdminMunicipalitySetting,
  useUploadAdminMunicipalityLogo
} from 'api/hooks/use-admin-municipality-settings';
import { useAdminMunicipality } from 'api/hooks/use-admin-municipalities';

const STRING_FIELDS = [
  'fiscal_year_start_month',
  'fiscal_year_start_day',
  'color_1', 'color_2', 'color_3', 'color_4', 'color_5',
  'department_segment',
  'seg_one_name', 'seg_two_name', 'seg_three_name', 'seg_four_name',
  'seg_five_name', 'seg_six_name', 'seg_seven_name', 'seg_eight_name',
  'pcard_extract_key'
];
// Analytic thresholds are not settings: they live in analytic_configs and are
// edited in the Analytic configuration section passed in as `children`.
const NUMERIC_FIELD_NAMES = ['years_of_data', 'active_window_years'];

// A default Bootstrap button's height. Padding the form by it makes the pinned
// Save Settings scroll away before Save configuration pins in the same spot.
const PINNED_BUTTON_HEIGHT = 'calc(1.5em + .75rem + 2px)';

const TEXT_FIELDS = [...STRING_FIELDS, ...NUMERIC_FIELD_NAMES];

const buildFormState = (settings) => {
  const state = {};
  TEXT_FIELDS.forEach((name) => {
    state[name] = settings?.[name] ?? '';
  });
  state.ai_search_enabled = settings?.ai_search_enabled ?? false;
  return state;
};

const MunicipalitySettingsForm = ({ municipalityId, children }) => {
  const { data: settings, isLoading } = useAdminMunicipalitySetting({ municipalityId });
  // MunicipalityDetail already fetched this, so it reads from the react-query cache. Only used to
  // name the municipality in the archival command below; the id works there too.
  const { data: municipality } = useAdminMunicipality({ id: municipalityId });
  const archivalCommand = `rake "transactions:archive_aged[${municipality?.name || municipalityId}]"`;
  const updateMutation = useUpdateAdminMunicipalitySetting({
    municipalityId,
    options: {
      onSuccess: () => {
        toast.success('Settings updated successfully!');
      }
    }
  });
  const uploadLogoMutation = useUploadAdminMunicipalityLogo({ municipalityId });
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState(() => buildFormState(null));

  useEffect(() => {
    if (settings) {
      setFormData(buildFormState(settings));
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSend = Object.fromEntries(
      Object.entries(formData).filter(([key, value]) => {
        if (key === 'ai_search_enabled') return true;
        // '' has to reach the server here: it clears the stored override.
        if (key === 'active_window_years' || key === 'pcard_extract_key') return true;
        return value !== '';
      })
    );
    updateMutation.mutate(dataToSend);
  };

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  const error = updateMutation.error;
  const isSubmitting = updateMutation.isLoading;

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    uploadLogoMutation.mutate(file);
  };

  const tocSections = [
    { id: 'logo', label: 'Logo' },
    { id: 'fiscal-year', label: 'Fiscal Year Settings' },
    { id: 'colors', label: 'Color Settings' },
    { id: 'segments', label: 'Segment Names' },
    { id: 'data', label: 'Data & AI Search' },
    { id: 'analytic-configuration', label: 'Analytic Configuration' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Card>
      <Card.Body>
        {error && (
          <Alert variant="danger">
            {error.response?.data?.message || error.message || 'An error occurred'}
          </Alert>
        )}

        <Row>
          {/* Table of Contents Sidebar */}
          <Col md={3} lg={2} className="d-none d-md-block">
            <div style={{ position: 'sticky', top: '20px' }}>
              <h6 className="mb-3 fw-bold">Table of Contents</h6>
              <nav className="nav flex-column">
                {tocSections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(section.id);
                    }}
                    className="nav-link text-primary py-1"
                    style={{ fontSize: '0.9rem' }}
                  >
                    {section.label}
                  </a>
                ))}
              </nav>
            </div>
          </Col>

          {/* Form Content */}
          <Col md={9} lg={10}>
            <Form onSubmit={handleSubmit} style={{ paddingBottom: PINNED_BUTTON_HEIGHT }}>
              {/* Zero height so it floats over the sections; sticky only within the form. */}
              <div className="sticky-top d-flex justify-content-end align-items-start" style={{ top: '20px', height: 0 }}>
                <Button type="submit" variant="primary" className="shadow-sm" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
              {/* Logo */}
              <h5 id="logo" className="mb-3 mt-4 border-bottom pb-2">Logo</h5>
              <Row className="mb-4">
                <Col md={6}>
                  {settings?.logo_url && (
                    <div className="mb-3">
                      <Image
                        src={settings.logo_url}
                        alt="Municipality logo"
                        style={{ maxHeight: '100px', maxWidth: '300px' }}
                        rounded
                      />
                    </div>
                  )}
                  <Form.Group>
                    <Form.Label>Upload Logo</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/svg+xml"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      disabled={uploadLogoMutation.isLoading}
                    />
                    <Form.Text className="text-muted">
                      Accepted formats: PNG, JPEG, GIF, SVG. Max 5MB.
                    </Form.Text>
                  </Form.Group>
                  {uploadLogoMutation.isLoading && (
                    <Spinner animation="border" size="sm" className="mt-2" />
                  )}
                  {uploadLogoMutation.isSuccess && (
                    <Alert variant="success" className="mt-2 py-1 px-2" dismissible>
                      Logo uploaded successfully.
                    </Alert>
                  )}
                  {uploadLogoMutation.isError && (
                    <Alert variant="danger" className="mt-2 py-1 px-2">
                      {uploadLogoMutation.error?.message || 'Failed to upload logo.'}
                    </Alert>
                  )}
                </Col>
              </Row>

              {/* Fiscal Year Settings */}
              <h5 id="fiscal-year" className="mb-3 mt-4 border-bottom pb-2">Fiscal Year Settings</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fiscal Year Start Month</Form.Label>
                    <Form.Control
                      type="number"
                      name="fiscal_year_start_month"
                      value={formData.fiscal_year_start_month}
                      onChange={handleChange}
                      min="1"
                      max="12"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fiscal Year Start Day</Form.Label>
                    <Form.Control
                      type="number"
                      name="fiscal_year_start_day"
                      value={formData.fiscal_year_start_day}
                      onChange={handleChange}
                      min="1"
                      max="31"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Color Settings */}
              <h5 id="colors" className="mb-3 mt-4 border-bottom pb-2">Color Settings</h5>
              <Row>
                {[1, 2, 3, 4, 5].map((num) => (
                  <Col md={4} key={num}>
                    <Form.Group className="mb-3">
                      <Form.Label>Color {num}</Form.Label>
                      <Form.Control
                        type="color"
                        name={`color_${num}`}
                        value={formData[`color_${num}`]}
                        onChange={handleChange}
                      />
                      <Form.Control
                        type="text"
                        name={`color_${num}`}
                        value={formData[`color_${num}`]}
                        onChange={handleChange}
                        placeholder="#FFFFFF"
                        className="mt-1"
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>

              {/* Segment Names */}
              <h5 id="segments" className="mb-3 mt-4 border-bottom pb-2">Segment Names</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Department Segment</Form.Label>
                    <Form.Control
                      type="text"
                      name="department_segment"
                      value={formData.department_segment}
                      onChange={handleChange}
                      placeholder="e.g. 3"
                    />
                    <Form.Text className="text-muted">
                      Which account-code segment number identifies the department (used by the prep Lambda).
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                {[
                  { num: 1, name: 'seg_one_name' },
                  { num: 2, name: 'seg_two_name' },
                  { num: 3, name: 'seg_three_name' },
                  { num: 4, name: 'seg_four_name' },
                  { num: 5, name: 'seg_five_name' },
                  { num: 6, name: 'seg_six_name' },
                  { num: 7, name: 'seg_seven_name' },
                  { num: 8, name: 'seg_eight_name' }
                ].map(({ num, name }) => (
                  <Col md={6} key={num}>
                    <Form.Group className="mb-3">
                      <Form.Label>Segment {num} Name</Form.Label>
                      <Form.Control
                        type="text"
                        name={name}
                        value={formData[name]}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>

              {/* Data & AI Search */}
              <h5 id="data" className="mb-3 mt-4 border-bottom pb-2">Data & AI Search</h5>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Years of Data</Form.Label>
                    <Form.Control
                      type="text"
                      name="years_of_data"
                      value={formData.years_of_data}
                      onChange={handleChange}
                      placeholder='positive integer or "all"'
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  {/* Bounds mirror MunicipalitySetting::MAX_ACTIVE_WINDOW_YEARS, which is the
                      authority; these only catch a typo before the round trip. */}
                  <Form.Group className="mb-3">
                    <Form.Label>Active Window (Years)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="10"
                      step="1"
                      name="active_window_years"
                      value={formData.active_window_years}
                      onChange={handleChange}
                      placeholder="default 5"
                    />
                    <Form.Text className="text-muted">
                      How far back the transaction grids, dashboard charts and
                      report tables reach. Leave blank for the 5-year default;
                      raise it (up to 10) only for a district that audits
                      further back, since a wider window means heavier queries.
                      <strong className="d-block mt-1">
                        A change here takes effect on the next nightly data
                        run. To apply it sooner, ask an engineer to run{' '}
                        <code>{archivalCommand}</code>.
                      </strong>
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3 mt-4 pt-2">
                    <Form.Check
                      type="checkbox"
                      name="ai_search_enabled"
                      label="AI Search Enabled"
                      checked={formData.ai_search_enabled}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Oracle PCard Extract Key</Form.Label>
                    <Form.Control
                      type="text"
                      name="pcard_extract_key"
                      value={formData.pcard_extract_key}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted">
                      S3 key of the monthly Oracle PCard extract file, inside the
                      municipality&apos;s upload bucket. Setting it makes an Oracle
                      Fusion Cloud ERP PCard municipality merge that extract on each
                      refresh; leave blank to run the Oracle PCard pipeline directly.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

            </Form>
            {children}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default MunicipalitySettingsForm;
